<template>
  <div
    id="content"
    itemscope
    itemtype="https://schema.org/Product"
    data-elbglobals="page_type:product_detail"
  >
    <AppBanner />
    <AlertBanner
      v-if="!calculated.isVisible"
      :text="translate('alert_message_preview_mode')"
    />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader />
    <main id="page-template">
      <ThePdpTemplate />
    </main>
    <!-- Lazy again: the footer's content comes from five CmsBlocks whose
         loader is fire-and-forget (useBlock), so a server render ships
         placeholders and five requests that outlive it. Server-rendering the
         footer needs SSR-aware CMS blocks first (VIP-12038). -->
    <LazyComponent id="footer" :delay="1500">
      <TheFooter />
    </LazyComponent>
  </div>
</template>

<script>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import TheHeader from '@/components/basic/organisms/TheHeader.vue'
import ThePdpTemplate from '@/components/pdp/templates/ThePdpTemplate.vue'
import {
  containsPrivateInfo,
  NO_INDEX_FOLLOW
} from '@/services/helpers/MetaHelper.js'
import { buildProductJsonLd } from '@/services/helpers/StructuredDataHelper'
import SiteConfig from '@/services/SiteConfig'
import { usePdpStore } from '@/stores/pdp'
import { mapActions, mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const AlertBanner = defineAsyncComponent(
  () => import('@/components/basic/atoms/AlertBanner.vue')
)
const OPEN_MOBILE_APP_PARAM = 'vic_app'

export default {
  head() {
    return {
      title: this.title,
      meta: [
        {
          name: 'robots',
          content: containsPrivateInfo()
            ? NO_INDEX_FOLLOW
            : this.product?.robots
        },
        { name: 'description', content: this.description },
        ...this.openGraphMeta
      ],
      link: [
        // Only with a URL: an empty <link rel="canonical"> is invalid and
        // SEO tooling flags it — the tag must be absent, not bare
        ...(this.canonicalHref
          ? [{ rel: 'canonical', href: this.canonicalHref }]
          : []),
        ...this.alternates
      ],
      // Product schema as JSON-LD, server-rendered with the rest of the
      // head (VIP-12038)
      script: this.productJsonLd
        ? [{ type: 'application/ld+json', innerHTML: this.productJsonLd }]
        : []
    }
  },
  components: {
    LazyComponent,
    AppBanner,
    AlertBanner,
    TheHeader,
    ThePdpTemplate,
    TheFooter,
    UspBar,
    TheCustomerMetaNavigation
  },
  computed: {
    ...mapState(usePdpStore, {
      product: 'getApiProduct',
      productUpdating: 'isUpdating',
      calculated: 'getCalculated'
    }),
    title() {
      const manufacturer = this.product?.manufacturer_name ?? ''
      const productName = this.product?.name ?? ''

      return `${manufacturer} ${productName}`.trim()
    },
    description() {
      return (
        this.product?.meta_description ??
        this.translate('fallback_meta_description')
      )
    },
    canonicalHref() {
      // The API resolves the stored canonical id into the master campaign's
      // URL, so canonical_for is set exactly when this page should
      // consolidate elsewhere; otherwise the product's own url is canonical.
      // Never the request path: the route matches any slug prefix (including
      // the -col-c collection paths), so that would hand every spelling of
      // the same product its own canonical (VIP-12038).
      const master = this.product?.canonical_for
      if (typeof master === 'string' && master) return master
      const own = this.product?.url
      if (typeof own === 'string' && own) return own
      return null
    },
    alternates() {
      const alternates =
        this.product?.alternates?.map((alternate) => ({
          rel: 'alternate',
          hreflang: alternate.hreflang,
          href: alternate.href
        })) || []
      if (alternates.length === 0) return alternates

      // Google wants the set self-referencing, and an x-default; the API only
      // lists the *other* sites. x-default is the .de page — the canonical on
      // the .de site itself, the de-DE alternate elsewhere.
      const canonical = this.canonicalHref
      const self = canonical
        ? [{ rel: 'alternate', hreflang: SiteConfig.lang, href: canonical }]
        : []
      const defaultHref =
        SiteConfig.lang === 'de-DE'
          ? self[0]?.href
          : alternates.find((alternate) => alternate.hreflang === 'de-DE')?.href
      const xDefault = defaultHref
        ? [{ rel: 'alternate', hreflang: 'x-default', href: defaultHref }]
        : []
      return [...alternates, ...self, ...xDefault]
    },
    // ProductHeroImage owns the responsive LCP preload because it also owns
    // the matching srcset and sizes. Keeping those values together prevents a
    // preload URL from diverging from the candidate the browser selects.
    openGraphMeta() {
      if (!this.product?.product_id) return []
      return [
        { property: 'og:type', content: 'product' },
        { property: 'og:title', content: this.title },
        { property: 'og:description', content: this.description },
        { property: 'og:site_name', content: SiteConfig.name },
        ...(this.canonicalHref
          ? [{ property: 'og:url', content: this.canonicalHref }]
          : []),
        ...(this.calculated.smallImageSrcPrefixed
          ? [
              {
                property: 'og:image',
                content: this.calculated.smallImageSrcPrefixed
              }
            ]
          : [])
      ]
    },
    productJsonLd() {
      if (!this.product?.product_id) return null
      try {
        return buildProductJsonLd(this.product, this.canonicalHref)
      } catch (error) {
        // A head computed throws straight through renderToString, so a
        // payload the schema builder cannot read (no stocks, say) would cost
        // the whole page. Structured data is the expendable part (VIP-12038).
        console.error('[PDP] product JSON-LD skipped', error)
        return null
      }
    }
  },
  methods: {
    ...mapActions(usePdpStore, [
      'refreshProduct',
      'loadProductForServerRender',
      'consumeSsrHydration',
      'fetchMetaProduct',
      'applyClientOnlyCalculatedFields',
      'refreshProductForAdminSession'
    ]),
    async routeChangeHandler(productId, campaignId) {
      if (this.consumeSsrHydration({ productId, campaignId })) {
        // Server-rendered: the product is already in the store, so skip the
        // refetch. Only the client concerns remain — the meta product
        // (tracking, personalization), the one calculated field whose input
        // the anonymous render does not have (the Miles&More cookie), and
        // the admin session, whose product payload differs from the
        // anonymous one (VIP-12351). Unlike the cold path there is no
        // refreshProduct catch around these, so rejections are reported
        // explicitly.
        this.fetchMetaProduct(productId, campaignId).catch((error) =>
          this.reportHydrationError(error)
        )
        this.applyClientOnlyCalculatedFields().catch((error) =>
          this.reportHydrationError(error)
        )
        this.refreshProductForAdminSession({ productId, campaignId }).catch(
          (error) => this.reportHydrationError(error)
        )
      } else {
        await this.refreshProduct({ productId, campaignId })
      }
      void this.productChangeHandler()
      void this.openMobileAppHandler({ productId, campaignId })
    },
    async reportHydrationError(error) {
      const { default: ApiController } =
        await import('@/services/ApiController')
      ApiController.logFrontendError(error, 'pdp_hydrated_entry', 'notice')
    },
    trackViewItem() {
      setTimeout(async () => {
        const [{ default: GA4Controller }, { default: AnalyticsItemFactory }] =
          await Promise.all([
            await import('@/services/analytics/GA4Controller'),
            await import('@/modules/tag_manager/AnalyticsItemFactory')
          ])
        void GA4Controller.pushViewItemEvent({
          item: AnalyticsItemFactory.getAnalyticsItemFromApiProduct(
            this.product
          )
        })
        void GA4Controller.pushChangePageEvent()
      }, 0)
    },
    async openMobileAppHandler({ productId, campaignId }) {
      const { default: NavigationHelper } =
        await import('@/services/helpers/NavigationHelper')
      if (
        this.$router.currentRoute.value.query[OPEN_MOBILE_APP_PARAM] === 'true'
      ) {
        NavigationHelper.openMobileAppPdp({ productId, campaignId })
      }
    },
    async productChangeHandler() {
      this.trackViewItem()
      const { default: DomHelper } =
        await import('@/services/helpers/DomHelper')
      if (!this.$route.hash) {
        void DomHelper.scrollIntoView('content')
      }
    }
  },
  // Runs during renderToString on the server — the mounted-phase hooks below
  // never do (VIP-12038). The loaded state serializes into the SSR payload.
  serverPrefetch() {
    const { pId, cId } = this.$route.params
    return this.loadProductForServerRender({ productId: pId, campaignId: cId })
  },
  beforeRouteEnter({ params: { pId, cId } }, _, next) {
    next((vm) => {
      vm.routeChangeHandler(pId, cId)
    })
  },
  beforeRouteUpdate({ params: { pId, cId } }, _, next) {
    this.routeChangeHandler(pId, cId)
    next()
  }
}
</script>
<style scoped>
.fade-enter-active {
  transition: opacity 1s var(--transition-timing-cb-vicampo) 0.5s;
}

.fade-leave-active {
  transition: opacity 0.5s var(--transition-timing-cb-vicampo);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Exactly the bar's height, from the shared token: 100px against a 115px bar
   left the footer's last rows behind it (VIP-12038). */
#main-footer {
  margin-bottom: var(--pdp-mobile-cart-bar-height);
}

/* A media query, not Vuetify's display class: the class comes from the
   JS breakpoint, which a server render can only guess from the user agent —
   a desktop UA in a narrow window keeps "lg" until something resizes, so the
   spacing would collapse while the (media-query gated) mobile cart bar is on
   screen, hiding the last 115px of the footer behind it. lg = 1280, the same
   boundary d-lg-none uses for the bar (VIP-12038). */
@media screen and (min-width: 1280px) {
  #main-footer {
    margin-bottom: 0;
  }
}
</style>
